import type { QuestionField } from "@/types/question";

// A dependent (child) question stores its parent in `validationRules`:
// `{ shouldShowIf: "Yes", dependentQuestionId: <parent id> }` — it's only
// shown once the parent is answered "Yes" (see AddQuestionModal's submit).

// `questionTypeOptions`' "Yes/No" value — the only type a question can
// depend on, since the condition is "parent answered Yes".
export const YES_NO_FIELD_TYPE = "radio";

export const isYesNoQuestion = (question: QuestionField) => question.fieldType === YES_NO_FIELD_TYPE;

export const getParentQuestionId = (question: QuestionField): number | null => {
  const rules = question.validationRules;
  if (rules?.shouldShowIf !== "Yes") return null;
  const parentId = Number(rules?.dependentQuestionId);
  return Number.isFinite(parentId) && parentId > 0 ? parentId : null;
};

export const getDependentQuestions = (parentId: number, questions: QuestionField[]) =>
  questions.filter((question) => getParentQuestionId(question) === parentId);

export interface OrderViolation {
  child: QuestionField;
  parent: QuestionField;
}

// A dependent question has to come after the question it depends on.
// Returns the first pair that breaks that in `orderedQuestions`, or null
// when the order is valid. Pairs whose parent isn't in the list (e.g. not
// loaded yet) can't be checked and are skipped.
export const findOrderViolation = (orderedQuestions: QuestionField[]): OrderViolation | null => {
  const indexById = new Map(orderedQuestions.map((question, index) => [question.id, index]));

  for (const [childIndex, child] of orderedQuestions.entries()) {
    const parentId = getParentQuestionId(child);
    if (parentId === null) continue;
    const parentIndex = indexById.get(parentId);
    if (parentIndex !== undefined && parentIndex > childIndex) {
      return { child, parent: orderedQuestions[parentIndex]! };
    }
  }
  return null;
};

export const getOrderViolationMessage = ({ child, parent }: OrderViolation) =>
  `"${child.label}" depends on "${parent.label}", so it must come after it.`;
